// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "up next" — the user's next (or ongoing) calendar event, fed by the
// auto-presence poll and re-evaluated on a slow tick for the countdown.

import type {KonvoCalendarEvent} from '@app/features/konvo_calendar/state/KonvoCalendar';
import {VisiblePoll} from '@app/features/ui/utils/VisiblePoll';
import {makeAutoObservable, onBecomeObserved, onBecomeUnobserved} from 'mobx';

const TICK_MS = 30 * 1000;
const LOOKAHEAD_MS = 2 * 60 * 60 * 1000;

class KonvoUpNextStore {
	events: Array<KonvoCalendarEvent> = [];
	now = Date.now();
	private clock = new VisiblePoll(TICK_MS, () => this.tick(), true);

	constructor() {
		makeAutoObservable<this, 'clock'>(this, {clock: false}, {autoBind: true});
		onBecomeObserved(this, 'now', () => this.clock.start());
		onBecomeUnobserved(this, 'now', () => this.clock.stop());
	}

	private tick(): void {
		this.now = Date.now();
	}

	setEvents(events: Array<KonvoCalendarEvent>): void {
		this.events = events;
		this.now = Date.now();
	}

	/** The ongoing event, or the next one starting within the lookahead. */
	get upNext(): KonvoCalendarEvent | null {
		const now = this.now;
		let best: KonvoCalendarEvent | null = null;
		let bestStart = Number.POSITIVE_INFINITY;
		for (const event of this.events) {
			if (event.is_all_day || event.is_cancelled || !event.start || !event.end) continue;
			const start = Date.parse(event.start);
			const end = Date.parse(event.end);
			if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
			if (end <= now || start - now > LOOKAHEAD_MS) continue;
			if (start < bestStart) {
				bestStart = start;
				best = event;
			}
		}
		return best;
	}

	get isOngoing(): boolean {
		const event = this.upNext;
		if (!event?.start) return false;
		return Date.parse(event.start) <= this.now;
	}

	/** "Now" while ongoing, otherwise minutes until start (e.g. "12m"). */
	get countdownLabel(): string | null {
		const event = this.upNext;
		if (!event?.start) return null;
		const start = Date.parse(event.start);
		if (start <= this.now) return 'Now';
		return `${Math.max(1, Math.round((start - this.now) / 60_000))}m`;
	}

	get startTimeLabel(): string | null {
		const event = this.upNext;
		if (!event?.start) return null;
		const date = new Date(event.start);
		return Number.isNaN(date.getTime())
			? null
			: date.toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});
	}
}

export default new KonvoUpNextStore();
